/**
 * Qué campos de un plan comercial definen el producto, y cuáles solo lo describen.
 *
 * Un plan es lo que se le vendió a alguien. Cambiarle la capacidad, los timbres o el
 * precio reescribe el producto que un contador contrató, así que esos campos no se
 * sobreescriben: se publica una versión nueva y la anterior sale de la venta.
 *
 * Espejo de `CAMPOS_DE_PRODUCTO` en `backend/apps/seguridad/licencias.py`, que es quien
 * lo hace cumplir: el `PATCH` del API rechaza un cambio de producto aunque alguien se
 * brinque la pantalla. Esto solo decide qué avisar antes de guardar.
 */

export const CAMPOS_DE_PRODUCTO = [
  'capacidad_rfc', 'timbres_incluidos', 'precio', 'max_usuarios',
  'reemplazos_incluidos', 'dias_retencion', 'max_sustituciones_slot',
  'vigencia_dias', 'modulos', 'complementos', 'permite_bloques',
  'tamano_bloque', 'precio_bloque', 'timbres_bloque', 'es_interno',
]

export const CAMPOS_DE_PRESENTACION = ['nombre', 'descripcion', 'activo']

/** Cómo se nombra cada campo en el aviso, para que se lea como español y no como columna. */
export const ETIQUETA_CAMPO = {
  capacidad_rfc: 'capacidad (RFC)',
  timbres_incluidos: 'timbres incluidos',
  precio: 'precio',
  max_usuarios: 'usuarios permitidos',
  reemplazos_incluidos: 'reemplazos',
  dias_retencion: 'días de retención',
  max_sustituciones_slot: 'sustituciones por lugar',
  vigencia_dias: 'vigencia',
  modulos: 'módulos',
  complementos: 'complementos',
  permite_bloques: 'venta por bloques',
  tamano_bloque: 'RFC por bloque',
  precio_bloque: 'precio por bloque',
  timbres_bloque: 'timbres por bloque',
  es_interno: 'paquete interno',
}

/**
 * Compara sin que un cambio de tipo o de orden pase por modificación.
 *
 * El formulario manda `'1750.00'` donde el API devuelve `1750.00`, y las listas de
 * módulos llegan en otro orden. Sin esto, abrir el modal y guardar sin tocar nada
 * publicaría una versión nueva.
 */
export const mismoValor = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort())
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') return Boolean(a) === Boolean(b)
  const na = parseFloat(a)
  const nb = parseFloat(b)
  if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb
  return String(a ?? '') === String(b ?? '')
}

/**
 * Qué campos de definición de producto cambia `datos` respecto a `original`.
 * Vacío si el cambio es solo de presentación.
 */
export const camposDeProductoModificados = (original, datos) =>
  CAMPOS_DE_PRODUCTO.filter((c) => c in (datos || {}) && !mismoValor(original?.[c], datos[c]))

/** Los campos cambiados, escritos para que los lea una persona. */
export const describirCambios = (campos) =>
  (campos || []).map((c) => ETIQUETA_CAMPO[c] || c).join(', ')
